import React, { useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Building2 } from "lucide-react";
import LegalLayout, { EmailLink, useLegalContent } from "./LegalLayout";
import { PRIVACY_EMAIL } from "./legalConfig";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const EMPTY = { name: "", email: "", company: "", employeeId: "", scope: "all", notes: "" };

/* There is no public support endpoint in user-service (only the sales
   demo-request mailer), so the form composes a pre-filled email to the
   privacy inbox. The request then lives in that inbox as the record. */
function DeletionForm({ t }) {
  const f = t.del.form;
  const [values, setValues] = useState(EMPTY);
  const [invalid, setInvalid] = useState(false);
  const [sent, setSent] = useState(false);

  const set = (key) => (e) => setValues((v) => ({ ...v, [key]: e.target.value }));

  const onSubmit = (e) => {
    e.preventDefault();
    const clean = Object.fromEntries(
      Object.entries(values).map(([k, v]) => [k, String(v).trim()])
    );
    if (clean.name.length < 2 || !EMAIL_RE.test(clean.email) || clean.company.length < 2) {
      setInvalid(true);
      setSent(false);
      return;
    }
    setInvalid(false);
    const scopeLabel = clean.scope === "all" ? f.scopeAll : f.scopePart;
    const href =
      `mailto:${PRIVACY_EMAIL}` +
      `?subject=${encodeURIComponent(`${f.mailSubject} - ${clean.company}`)}` +
      `&body=${encodeURIComponent(f.mailBody(clean, scopeLabel))}`;
    window.location.href = href;
    setSent(true);
  };

  return (
    <form className="nxlg-form" onSubmit={onSubmit} noValidate>
      <div className="nxlg-field">
        <label htmlFor="del-name">{f.name}</label>
        <input id="del-name" autoComplete="name" value={values.name} onChange={set("name")} required maxLength={120} />
      </div>
      <div className="nxlg-field">
        <label htmlFor="del-email">{f.email}</label>
        <input id="del-email" type="email" dir="ltr" autoComplete="email" inputMode="email" value={values.email} onChange={set("email")} required maxLength={160} />
      </div>
      <div className="nxlg-field">
        <label htmlFor="del-company">{f.company}</label>
        <input id="del-company" autoComplete="organization" value={values.company} onChange={set("company")} required maxLength={120} />
      </div>
      <div className="nxlg-field">
        <label htmlFor="del-empid">{f.employeeId}</label>
        <input id="del-empid" value={values.employeeId} onChange={set("employeeId")} maxLength={40} />
      </div>
      <fieldset className="nxlg-field nxlg-radios">
        <legend>{f.scope}</legend>
        <label>
          <input type="radio" name="scope" value="all" checked={values.scope === "all"} onChange={set("scope")} />
          {f.scopeAll}
        </label>
        <label>
          <input type="radio" name="scope" value="part" checked={values.scope === "part"} onChange={set("scope")} />
          {f.scopePart}
        </label>
      </fieldset>
      <div className="nxlg-field">
        <label htmlFor="del-notes">{f.notes}</label>
        <textarea id="del-notes" rows={4} value={values.notes} onChange={set("notes")} maxLength={1000} />
      </div>

      {invalid && <p className="nxlg-error" role="alert">{f.required}</p>}
      {sent && (
        <div className="nxlg-success" role="status">
          <CheckCircle2 size={18} aria-hidden="true" />
          <div>
            <p>{f.sent}</p>
            <p>
              {f.fallback} <a href={`mailto:${PRIVACY_EMAIL}`}><bdi>{PRIVACY_EMAIL}</bdi></a>
            </p>
          </div>
        </div>
      )}

      <button type="submit" className="nxlg-btn">{f.submit}</button>
    </form>
  );
}

export default function DeleteAccountPage() {
  const { t } = useLegalContent();
  return (
    <LegalLayout docTitle={t.del.docTitle}>
      {({ t: tr }) => (
        <article className="nxlg-doc">
          <h1>{tr.del.title}</h1>
          {tr.del.intro.map((p, i) => <p key={i} className="nxlg-intro">{p}</p>)}

          <section className="nxlg-section">
            <h2>{tr.del.stepsTitle}</h2>
            <ol className="nxlg-steps">
              {tr.del.steps.map((s, i) => <li key={i}>{s}</li>)}
            </ol>
            <p className="nxlg-note">
              <Building2 size={16} aria-hidden="true" />
              <span>{tr.del.employerTip}</span>
            </p>
          </section>

          <section className="nxlg-section nxlg-card">
            <h2>{tr.del.formTitle}</h2>
            <DeletionForm t={tr} />
          </section>

          <section className="nxlg-section">
            <h2>{tr.del.deletedTitle}</h2>
            <ul>{tr.del.deleted.map((s, i) => <li key={i}>{s}</li>)}</ul>
          </section>

          <section className="nxlg-section">
            <h2>{tr.del.retainedTitle}</h2>
            {tr.del.retained.map((s, i) => <p key={i}>{s}</p>)}
          </section>

          <section className="nxlg-section">
            <h2>{tr.del.timeframeTitle}</h2>
            <p>{tr.del.timeframe}</p>
          </section>

          <section className="nxlg-section">
            <h2>{tr.del.emailTitle}</h2>
            <p>{tr.del.emailText}</p>
            <EmailLink subject={tr.del.form.mailSubject} />
          </section>

          <p className="nxlg-meta">
            <Link to="/privacy" className="nxlg-inline-link">{tr.common.privacyLink}</Link>
          </p>
        </article>
      )}
    </LegalLayout>
  );
}
